import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../lib/store';
import { useGetConceptGroupsQuery } from '../../api/createProjectApiSlice';
import { AddedConcept, setConceptGroupName } from '../../state/createProjectSlice';
import { selectAddedConcepts } from '../../state/selector';
import { ConceptGroupResponse } from '../../types/response';

interface UseConceptAutocompleteProps {
  concept: AddedConcept;
}

interface UseConceptAutocompleteResults {
  conceptGroups: ConceptGroupResponse[];
  isConceptGroupsLoading: boolean;
  handleChange: (
    event: React.ChangeEvent<object>,
    selectedOption: ConceptGroupResponse | null
  ) => void;
  inputVal: string;
  setInputVal: (inputVal: string) => void;
  addedConceptNames: string[];
  isUserEditing: boolean;
  setIsUserEditing: (val: boolean) => void;
}
export const useConceptAutocomplete = ({
  concept,
}: UseConceptAutocompleteProps): UseConceptAutocompleteResults => {
  const [inputVal, setInputVal] = useState<string>(concept.conceptGroupName || '');
  const [debouncedInput, setDebouncedInput] = useState(inputVal);
  const [isUserEditing, setIsUserEditing] = useState(false);

  useEffect(() => {
    setInputVal(concept.conceptGroupName || '');
  }, [concept.conceptGroupName]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInput(inputVal);
    }, 400);

    return () => clearTimeout(timer);
  }, [inputVal]);

  const shouldFetchInitial = concept.conceptGroupName && debouncedInput === '';
  const {
    data: conceptGroups,
    isLoading: isConceptGroupsLoading,
    isFetching: isConceptGroupsFetching,
  } = useGetConceptGroupsQuery(
    shouldFetchInitial ? { search: concept.conceptGroupName } : { search: debouncedInput },
    { skip: !shouldFetchInitial && debouncedInput === '' }
  );

  const addedConcepts = useAppSelector(selectAddedConcepts);
  const addedConceptNames = addedConcepts.map((concept: AddedConcept) => concept.conceptGroupName);

  const dispatch = useAppDispatch();
  const handleChange = (
    _event: React.ChangeEvent<object>,
    selectedOption: ConceptGroupResponse | null
  ) => {
    if (!selectedOption) return;
    setIsUserEditing(false);
    const newConcept = {
      ...concept,
      conceptGroupName: selectedOption.label,
      conceptGroupId: selectedOption.id,
    };
    dispatch(setConceptGroupName(newConcept));
  };

  return {
    conceptGroups: conceptGroups?.data ?? [],
    isConceptGroupsLoading: isConceptGroupsLoading || isConceptGroupsFetching,
    handleChange,
    inputVal,
    setInputVal,
    addedConceptNames,
    isUserEditing,
    setIsUserEditing,
  };
};
